import React, { useState } from 'react';
import { 
    AppBar, 
    Toolbar, 
    Typography, 
    Button, 
    IconButton, 
    Stack, 
    Tooltip, 
    useTheme,
    alpha,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Box,
    Divider
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';

// Icons
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import DvrIcon from '@mui/icons-material/Dvr';
import InfoIcon from '@mui/icons-material/Info';
import MenuIcon from '@mui/icons-material/Menu';
import MailIcon from '@mui/icons-material/Mail';
import { useAtomValue, useSetAtom } from 'jotai';
import { isLoggedInAtom, sessionAtom, expiryAtom, userAtom } from '../../atoms/auth';
import { RESET } from 'jotai/utils';
import { useColorMode } from '../../theme/themeContext';

const Navbar = () => {
    const theme = useTheme();
    const { toggleColorMode } = useColorMode();
    const navigate = useNavigate();
    const location = useLocation(); // Get current route

    // Jotai Auth State
    const isUserLoggedIn = useAtomValue(isLoggedInAtom);
    const setSession = useSetAtom(sessionAtom);
    const setExpiry = useSetAtom(expiryAtom);
    const setUser = useSetAtom(userAtom);

    // State for mobile menu
    const [anchorElNav, setAnchorElNav] = useState(null);

    const handleOpenNavMenu = (event) => {
        setAnchorElNav(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    const handleLogout = () => {
        // Reset atoms (automatically clears localStorage)
        setSession(RESET);
        setExpiry(RESET);
        setUser(RESET);
        navigate('/');
    };

    // Helper function to check if a path is active
    const isActive = (path) => {
        if (path === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(path);
    };

    // Define menu items based on auth state
    const pages = isUserLoggedIn 
        ? [
            { name: 'Logs', path: '/logs/email-flow', icon: <DvrIcon fontSize="small" /> },
            // { name: 'Recall', path: '/email/recall', icon: <MailIcon fontSize="small" /> },
            { name: 'About', path: '/about', icon: <InfoIcon fontSize="small" /> },
          ]
        : [
            { name: 'About', path: '/about', icon: <InfoIcon fontSize="small" /> },
          ];

    // Get active button styles
    const getButtonStyles = (path) => ({
        color: isActive(path) ? 'primary.main' : 'text.primary',
        textTransform: 'none',
        fontWeight: isActive(path) ? 600 : 500,
        fontSize: '0.95rem',
        px: 2,
        py: 1,
        borderRadius: 2,
        position: 'relative',
        backgroundColor: isActive(path) 
            ? alpha(theme.palette.primary.main, 0.1) 
            : 'transparent',
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08)
        },
        // Active indicator - bottom border
        '&::after': isActive(path) ? {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '60%',
            height: '3px',
            backgroundColor: theme.palette.primary.main,
            borderRadius: '3px 3px 0 0',
            transition: 'width 0.3s ease',
        } : {},
        // Expand underline on hover for active items
        '&:hover::after': isActive(path) ? {
            width: '80%',
        } : {},
    });

    // Get active menu item styles
    const getMenuItemStyles = (path) => ({
        py: 1.5,
        backgroundColor: isActive(path) 
            ? alpha(theme.palette.primary.main, 0.08) 
            : 'transparent',
        borderLeft: isActive(path) 
            ? `3px solid ${theme.palette.primary.main}` 
            : '3px solid transparent',
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
        },
    });

    return (
        <AppBar 
            position="fixed" 
            color="inherit"
            elevation={0}
            sx={{
                backdropFilter: 'blur(20px)',
                backgroundColor: alpha(theme.palette.background.paper, 0.85),
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: 'all 0.3s ease'
            }}
        >
            <Toolbar sx={{ 
                justifyContent: 'space-between',
                minHeight: { xs: 64, sm: 70 },
                px: { xs: 2, sm: 3, md: 4 }
            }}>
                
                {/* --- DESKTOP LOGO --- */}
                <Typography 
                    variant="h6" 
                    component={Link} 
                    to="/" 
                    sx={{ 
                        display: { xs: 'none', md: 'flex' },
                        textDecoration: 'none', 
                        color: 'text.primary', 
                        fontWeight: 700, 
                        fontSize: '1.35rem',
                        letterSpacing: '-0.5px',
                        alignItems: 'center',
                        gap: 1,
                        transition: 'color 0.2s ease',
                        '&:hover': {
                            color: 'primary.main'
                        }
                    }}
                >
                    <MailIcon sx={{ fontSize: 28 }} />
                    MailFlow Monitor
                </Typography>

                {/* --- MOBILE MENU (Hamburger) --- */}
                <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                    <IconButton
                        size="large"
                        aria-label="navigation menu"
                        aria-controls="menu-appbar"
                        aria-haspopup="true"
                        onClick={handleOpenNavMenu}
                        color="inherit"
                        sx={{
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            borderRadius: 2
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Menu
                        id="menu-appbar"
                        anchorEl={anchorElNav}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        keepMounted
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        open={Boolean(anchorElNav)}
                        onClose={handleCloseNavMenu}
                        sx={{ 
                            display: { xs: 'block', md: 'none' },
                            mt: 1
                        }}
                        PaperProps={{
                            sx: {
                                borderRadius: 2,
                                minWidth: 200,
                                boxShadow: theme.shadows[8]
                            }
                        }}
                    >
                        {/* Mobile Home Link */}
                        <MenuItem 
                            onClick={() => { handleCloseNavMenu(); navigate('/'); }}
                            sx={getMenuItemStyles('/')}
                        >
                            <ListItemIcon>
                                <HomeRoundedIcon 
                                    fontSize="small" 
                                    sx={{ color: isActive('/') ? 'primary.main' : 'inherit' }}
                                />
                            </ListItemIcon>
                            <ListItemText 
                                sx={{ 
                                    '& .MuiTypography-root': { 
                                        fontWeight: isActive('/') ? 600 : 400,
                                        color: isActive('/') ? 'primary.main' : 'inherit'
                                    } 
                                }}
                            >
                                Home
                            </ListItemText>
                        </MenuItem>

                        <Divider sx={{ my: 0.5 }} />

                        {/* Dynamic Pages */}
                        {pages.map((page) => (
                            <MenuItem 
                                key={page.name} 
                                onClick={() => { handleCloseNavMenu(); navigate(page.path); }}
                                sx={getMenuItemStyles(page.path)}
                            >
                                <ListItemIcon>
                                    {React.cloneElement(page.icon, {
                                        sx: { color: isActive(page.path) ? 'primary.main' : 'inherit' }
                                    })}
                                </ListItemIcon>
                                <ListItemText
                                    sx={{ 
                                        '& .MuiTypography-root': { 
                                            fontWeight: isActive(page.path) ? 600 : 400,
                                            color: isActive(page.path) ? 'primary.main' : 'inherit'
                                        } 
                                    }}
                                >
                                    {page.name}
                                </ListItemText>
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>

                {/* --- MOBILE LOGO (Centered) --- */}
                <Typography
                    variant="h6"
                    noWrap
                    component={Link}
                    to="/"
                    sx={{
                        display: { xs: 'flex', md: 'none' },
                        flexGrow: 1,
                        justifyContent: 'center',
                        textDecoration: 'none',
                        color: 'text.primary',
                        fontWeight: 700,
                        fontSize: '1.15rem',
                        letterSpacing: '-0.5px',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <MailIcon sx={{ fontSize: 24 }} />
                    MailFlow
                </Typography>

                {/* --- DESKTOP LINKS --- */}
                <Box sx={{ 
                    flexGrow: 1, 
                    display: { xs: 'none', md: 'flex' }, 
                    gap: 0.5,
                    ml: 4
                }}>
                    <Button
                        onClick={() => navigate('/')}
                        startIcon={
                            <HomeRoundedIcon 
                                fontSize="small" 
                                sx={{ color: isActive('/') ? 'primary.main' : 'inherit' }}
                            />
                        }
                        sx={getButtonStyles('/')}
                    >
                        Home
                    </Button>
                    {pages.map((page) => (
                        <Button
                            key={page.name}
                            onClick={() => navigate(page.path)}
                            startIcon={React.cloneElement(page.icon, {
                                sx: { color: isActive(page.path) ? 'primary.main' : 'inherit' }
                            })}
                            sx={getButtonStyles(page.path)}
                        >
                            {page.name}
                        </Button>
                    ))}
                </Box>

                {/* --- RIGHT ACTIONS (Theme & Auth) --- */}
                <Stack 
                    direction="row" 
                    alignItems="center" 
                    spacing={{ xs: 1, sm: 1.5 }}
                    sx={{ flexShrink: 0 }}
                >
                    <Tooltip title={`Switch to ${theme.palette.mode === 'dark' ? 'light' : 'dark'} mode`} arrow>
                        <IconButton 
                            onClick={toggleColorMode} 
                            color="inherit"
                            size="medium"
                            sx={{ 
                                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`, 
                                borderRadius: 2,
                                width: 40,
                                height: 40,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                    borderColor: alpha(theme.palette.primary.main, 0.3)
                                }
                            }}
                        >
                            {theme.palette.mode === 'dark' ? 
                                <Brightness7Icon sx={{ fontSize: 20 }} /> : 
                                <Brightness4Icon sx={{ fontSize: 20 }} />
                            }
                        </IconButton>
                    </Tooltip>

                    {/* Divider - Hidden on mobile */}
                    <Divider 
                        orientation="vertical" 
                        flexItem 
                        sx={{ 
                            display: { xs: 'none', sm: 'block' },
                            height: 32,
                            alignSelf: 'center',
                            mx: { sm: 1, md: 1.5 }
                        }} 
                    />

                    {isUserLoggedIn ? (
                        <Button 
                            onClick={handleLogout} 
                            variant="outlined" 
                            color="primary" 
                            startIcon={<LogoutRoundedIcon sx={{ fontSize: 18 }} />}
                            sx={{ 
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.9rem',
                                px: { xs: 1.5, sm: 2.5 },
                                py: { xs: 0.75, sm: 1 },
                                borderWidth: 1.5,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderWidth: 1.5,
                                    transform: 'translateY(-1px)',
                                    boxShadow: theme.shadows[2]
                                }
                            }}
                        >
                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                Logout
                            </Box>
                        </Button>
                    ) : (
                        <Button 
                            component={Link} 
                            to="/login" 
                            variant="contained" 
                            color="primary"
                            startIcon={<LoginRoundedIcon sx={{ fontSize: 18 }} />}
                            disableElevation
                            sx={{ 
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.9rem',
                                px: { xs: 1.5, sm: 2.5 },
                                py: { xs: 0.75, sm: 1 },
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: theme.shadows[4]
                                }
                            }}
                        >
                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                Login
                            </Box>
                        </Button>
                    )}
                </Stack>
            </Toolbar>
        </AppBar>
    );
};

export default Navbar;